import React, { useState } from "react";
import { Box } from "@chakra-ui/react";
import { toast } from "sonner";
import {
  startPlanCheckout,
  openBillingPortal,
  isNoStripeCustomerPortalError,
  isBillingAlreadySubscribedError,
  isThreeMonthOfferEndedError,
} from "../../services/billingApi";
import GradientButton from "../../views/Dashboard/Profile/components/GradientButton";
import type { BillingCheckoutInterval, BillingPlan, BillingStatusResponse } from "../../types/billing";
import {
  BILLING_PLAN_LABEL,
  deriveBillingUiState,
  resolveBillingPlan,
  resolveReportsPerWeek,
  resolveSimsPerReport,
  tier1CheckoutCtaLabel,
} from "../../types/billing";
import { hasTier1CheckoutAttempted, markTier1CheckoutAttempted } from "../../utils/billingCheckoutHint";
import { presentMutationError } from "../../utils/showApiErrorToast";

export async function beginPlanCheckout(
  plan: BillingPlan,
  interval: BillingCheckoutInterval = "month",
  options?: { onThreeMonthOfferEnded?: () => void }
): Promise<boolean> {
  try {
    const { url } = await startPlanCheckout(plan, interval);
    markTier1CheckoutAttempted();
    window.location.href = url;
    return true;
  } catch (e) {
    if (isBillingAlreadySubscribedError(e)) {
      toast.info("You already have an active subscription", {
        description: "Switch plans with Change plan on the Billing tab.",
      });
      return false;
    }
    if (isThreeMonthOfferEndedError(e)) {
      presentMutationError(e, "The 3-month offer is no longer available.");
      options?.onThreeMonthOfferEnded?.();
      return false;
    }
    presentMutationError(e, "Checkout failed");
    return false;
  }
}

interface SubscribePlanButtonProps {
  plan: BillingPlan;
  interval?: BillingCheckoutInterval;
  /** Overrides automatic label from billing status + local checkout hint */
  label?: string;
  /** From GET /billing/status — drives "Start trial" vs "Subscribe" when `label` is omitted */
  billingStatus?: BillingStatusResponse | null;
  /** After canceling checkout, prefer "Subscribe" even if local hint is unset */
  preferSubscribeCopy?: boolean;
  size?: "sm" | "md" | "lg";
}

/** Primary CTA: Stripe Checkout for Standard or Plus */
export function SubscribePlanButton({
  plan,
  interval = "month",
  label,
  billingStatus,
  preferSubscribeCopy = false,
  size = "md",
}: SubscribePlanButtonProps) {
  const [loading, setLoading] = useState(false);

  const resolvedLabel =
    label ??
    (preferSubscribeCopy
      ? `Subscribe to ${BILLING_PLAN_LABEL[plan]}`
      : `${tier1CheckoutCtaLabel(billingStatus ?? null, hasTier1CheckoutAttempted())} · ${BILLING_PLAN_LABEL[plan]}`);

  const handleClick = async () => {
    setLoading(true);
    const redirected = await beginPlanCheckout(plan, interval);
    if (!redirected) setLoading(false);
  };

  return (
    <GradientButton size={size} onClick={() => void handleClick()} isDisabled={loading} isLoading={loading}>
      {resolvedLabel}
    </GradientButton>
  );
}

interface SubscribeTier1ButtonProps {
  label?: string;
  billingStatus?: BillingStatusResponse | null;
  preferSubscribeCopy?: boolean;
  size?: "sm" | "md" | "lg";
}

/** @deprecated Prefer {@link SubscribePlanButton}. Starts Plus checkout. */
export function SubscribeTier1Button(props: SubscribeTier1ButtonProps) {
  return <SubscribePlanButton plan="plus" {...props} />;
}

interface ManageBillingButtonProps {
  size?: "sm" | "md" | "lg";
  label?: string;
  /**
   * When true (e.g. trialing/active), portal errors show the real message — not “Subscribe first”.
   * Avoids false positives when the API returns a vague 400 that still mentions “customer”.
   */
  suppressSubscribeFirstToast?: boolean;
}

/** Opens Stripe Customer Portal */
export function ManageBillingButton({
  size = "md",
  label = "Manage billing",
  suppressSubscribeFirstToast = false,
}: ManageBillingButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    setLoading(true);
    try {
      const { url } = await openBillingPortal();
      window.location.href = url;
    } catch (e) {
      if (!suppressSubscribeFirstToast && isNoStripeCustomerPortalError(e)) {
        toast.info("Subscribe first", {
          description: "Complete a subscription checkout before managing billing.",
        });
        setLoading(false);
        return;
      }
      presentMutationError(e, "Portal error");
      setLoading(false);
    }
  };

  const h = size === "lg" ? "40px" : size === "sm" ? "32px" : "36px";
  const px = size === "lg" ? 6 : size === "sm" ? 3 : 4;
  const fontSize = size === "lg" ? "md" : size === "sm" ? "sm" : "sm";

  return (
    <Box
      as="button"
      type="button"
      h={h}
      px={px}
      fontSize={fontSize}
      fontWeight="500"
      borderRadius="md"
      borderWidth="1px"
      borderColor="cyan.400"
      color="cyan.500"
      bg="transparent"
      cursor={loading ? "wait" : "pointer"}
      opacity={loading ? 0.7 : 1}
      pointerEvents={loading ? "none" : "auto"}
      _hover={{ bg: loading ? undefined : "cyan.50" }}
      _active={{ bg: loading ? undefined : "cyan.100" }}
      onClick={() => void handleClick()}
      disabled={loading}
    >
      {loading ? "Opening…" : label}
    </Box>
  );
}

export function BillingStatusSummary({ status }: { status: BillingStatusResponse | null }) {
  if (!status) {
    return (
      <Box as="p" fontSize="sm" color="gray.500">
        Unable to load subscription status.
      </Box>
    );
  }

  const raw = status.status;
  const ui = deriveBillingUiState(raw);
  const trialEnd = status.trial_end ? new Date(status.trial_end).toLocaleDateString() : null;
  const periodEnd = status.current_period_end ? new Date(status.current_period_end).toLocaleDateString() : null;
  const plan = resolveBillingPlan(status);
  // No active subscription = Free (the API still sends the legacy default tier, which maps to Plus).
  const planLabel = status.effective_plan === "free" ? "Free" : plan ? BILLING_PLAN_LABEL[plan] : null;
  const reportsCap = resolveReportsPerWeek(status);
  const reportsUsed = status.reports_used_this_week;
  const simsCap = resolveSimsPerReport(status);

  /** API may send null when there is no Stripe subscription yet */
  const statusLine =
    raw == null || (typeof raw === "string" && raw.trim() === "")
      ? "No active subscription"
      : String(raw);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box as="p" fontSize="sm" fontWeight="600">
        Plan: {planLabel ?? "None"} · {statusLine}
      </Box>
      {ui === "trialing" && trialEnd && (
        <Box as="p" fontSize="sm" color="gray.600">
          Trial ends {trialEnd}
        </Box>
      )}
      {(ui === "active" || ui === "trialing") && periodEnd && (
        <Box as="p" fontSize="sm" color="gray.600">
          {status?.cancel_at_period_end ? `Access until ${periodEnd}` : `Renews ${periodEnd}`}
        </Box>
      )}
      {reportsCap != null && reportsUsed != null && (
        <Box as="p" fontSize="sm" color="gray.600">
          Reports this week: {reportsUsed} / {reportsCap}
        </Box>
      )}
      {(ui === "active" || ui === "trialing") && (
        <Box as="p" fontSize="sm" color="gray.600">
          Simulations per report: {simsCap}
        </Box>
      )}
      {ui === "past_due" && (
        <Box as="p" fontSize="sm" color="orange.500">
          Payment failed — update your payment method in the billing portal.
        </Box>
      )}
      {ui === "canceled" && (
        <Box as="p" fontSize="sm" color="gray.600">
          Subscription canceled. Subscribe again to restore access.
        </Box>
      )}
    </Box>
  );
}
